import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { purchaseOrdersApi } from '../purchaseOrdersApi';

/**
 * Cancels an order, with the reason.
 *
 * **A modal because the reason is required**, and because cancelling is the state most easily
 * confused with deleting — the body says which this is before the tick.
 *
 * **AND BECAUSE IT CHANGES OTHER RECORDS.** Cancelling gives every quantity this order committed
 * back to the purchase requests it drew on, which is the half a reader would not predict from
 * the word alone. Stated before the tick rather than discovered afterwards.
 */
export function CancelPurchaseOrderModal({
  open,
  order,
  isSubmitted = false,
  onClose,
  onCancelled,
}: {
  open: boolean;
  order: { id: string; orderNumber: string; sourceRequestNumbers?: string | null };
  /** Whether an approval is pending on it, so the dialog can say it will be closed. */
  isSubmitted?: boolean;
  onClose: () => void;
  onCancelled: () => void;
}) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = reason.trim().length > 0 && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await purchaseOrdersApi.cancel(order.id, reason.trim());
      onCancelled();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not cancel this order.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'cancel-purchase-order' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Cancel ${order.orderNumber}`}
      size="lg"
      id="cancel-purchase-order-modal"
      /*
        NO Autogenerate. Why the organization decided not to buy something is a judgement
        somebody is making about a specific order — generating one would put words in their
        mouth, which is the one place the generator would be actively wrong.
      */
      confirm={{
        id: 'cancel-purchase-order-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="cancel-purchase-order-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            id="cancel-purchase-order-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="flex flex-col gap-2 text-xs text-slate-600">
          <p>
            <strong>Cancelled is an outcome the register keeps.</strong> The order stays visible,
            marked cancelled, with this reason against it.
          </p>

          {/*
            IT CHANGES OTHER RECORDS, which is the thing a reader cannot predict from the word.
            Naming the requests is what makes it checkable — they go back to being orderable, and
            somebody may be waiting for exactly that.
          */}
          <p>
            Every quantity this order committed goes back to{' '}
            {order.sourceRequestNumbers ? (
              <span className="font-mono text-slate-800">{order.sourceRequestNumbers}</span>
            ) : (
              'the purchase requests it drew on'
            )}
            , so those lines become orderable again.
          </p>

          <p>
            If it was keyed against the wrong record, or twice, <strong>Delete</strong> is the one
            you want instead — that hides it rather than recording an outcome.
          </p>

          {isSubmitted ? (
            <p>Any approval still pending on it is closed in the same action.</p>
          ) : null}
        </div>

        <Textarea
          id="cancel-purchase-order-reason"
          label="Reason"
          required
          description="Why the organization decided not to place this order. It stays on the record."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={reason.trim() ? error?.fieldError('reason') : 'Say why it is being cancelled.'}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
